import { stringify } from 'qs'

import { LS_API_DOMAIN, LS_APP_DOMAIN } from 'config/constants'
import { getEnvInfo } from 'utils/helpers/device-info'
import { setToCookie, removeCookie } from 'utils/helpers/cookie'
import {
  setTokenToNative,
  setRefreshTokenToNative,
} from 'utils/native-notifier'
import { APP_VERSION } from 'config/constants'

class RequestCore {
  // All living sinyi queries use this token,
  static #token = null

  // Should only be used by swr queries
  static getToken(token) {
    return this.#token
  }

  static setToken(token) {
    this.#token = token
  }

  static hasToken() {
    return !!this.#token
  }

  // set `authorization` to the options field of request methods(ex. lsGet)
  // for overwriting it in specified conditions if needed.
  static genHeaders(headers = {}) {
    return this.#token
      ? { authorization: `Bearer ${this.#token}`, ...headers }
      : headers
  }

  // used by swr queries
  static genHeadersWithToken(token, headers = {}) {
    return token ? { authorization: `Bearer ${token}`, ...headers } : headers
  }
}

export default RequestCore

// Check HTTP code from living sinyi API
const lsCheckHTTPStatus = (response, fetchArgs) => {
  if (response.status >= 200 && response.status < 400) {
    return response
  } else if (response.status === 401) {
    return UnauthorizeHandler.handleTokenRefresh(response.status, fetchArgs)
  }
  const error = new Error(response.statusText)
  error._response = response
  throw error
}

// Check living sinyi response successfully
const lsCheckResponseSuccess = (data, fetchArgs) => {
  if (data?.ok) {
    return data?.result
  } else if (data?.error?.code === 401) {
    return UnauthorizeHandler.handleTokenRefresh(data.error.code, fetchArgs)
  }
  const error = new Error(data?.msg)
  error._response = data.error
  throw error
}

const lsRequest = (...args) => {
  return fetch(...args)
    .then((response) => lsCheckHTTPStatus(response, args))
    .then((response) => response.json())
    .then((response) => lsCheckResponseSuccess(response, args))
}

// avoid try/catch handling by returning [data, error] array
// Usage: const [data, error] = await safeAwait(promise)
const nativeExceptions = [
  EvalError,
  RangeError,
  ReferenceError,
  SyntaxError,
  TypeError,
  URIError,
].filter((except) => typeof except === 'function')
function throwNative(error) {
  for (const Exception of nativeExceptions) {
    if (error instanceof Exception) throw error
  }
}
export const safeAwait = (promise) =>
  promise
    .then((data) => {
      if (data instanceof Error) {
        throwNative(data)
        return [data]
      }
      return [data]
    })
    .catch((error) => {
      throwNative(error)
      return [undefined, error]
    })

const getMethod = (domain, core) => (path, data, options) => {
  const qs = !!data && stringify(data)
  const headers = core?.genHeaders(options?.headers) || {}
  const isServer = typeof window === 'undefined'
  return lsRequest(`${domain}${path}` + (qs ? `?${qs}` : ''), {
    headers: isServer
      ? { ...headers, 'User-Agent': `LivingLife/${APP_VERSION}` }
      : { ...headers },
  })
}

// Used by seansitive data hooks
const getMethodWithToken = (domain, core) => (path, token, data, options) => {
  const headers = core?.genHeadersWithToken(token, options?.headers) || {}
  const isServer = typeof window === 'undefined'
  return lsRequest(`${domain}${path}` + (data ? `?${stringify(data)}` : ''), {
    headers: isServer
      ? { ...headers, 'User-Agent': `LivingLife/${APP_VERSION}` }
      : { ...headers },
  })
}

const postMethod = (domain, core) => (path, data = {}, options) =>
  lsRequest(domain + path + (options?.qs ? `?${stringify(options?.qs)}` : ''), {
    method: 'POST',
    headers: options?.uploadFile
      ? core?.genHeaders(options?.headers) || {}
      : {
          'Content-Type': 'application/json',
          ...(core?.genHeaders(options?.headers) || {}),
        },
    body: options?.uploadFile ? data : JSON.stringify(data),
  })

const putMethod = (domain, core) => (path, data = {}, options) =>
  lsRequest(domain + path + (options?.qs ? `?${stringify(options?.qs)}` : ''), {
    method: 'PUT',
    headers: {
      ...(core?.genHeaders(options?.headers) || {}),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })

const deleteMethod = (domain, core) => (path, data, options) =>
  lsRequest(`${domain}${path}` + (data ? `?${stringify(data)}` : ''), {
    method: 'DELETE',
    headers: core?.genHeaders(options?.headers) || {},
  })

export const lsGet = getMethod(LS_API_DOMAIN, RequestCore)
export const lsGetWithToken = getMethodWithToken(LS_API_DOMAIN, RequestCore)
export const lsPost = postMethod(LS_API_DOMAIN, RequestCore)
export const lsPut = putMethod(LS_API_DOMAIN, RequestCore)
export const lsDelete = deleteMethod(LS_API_DOMAIN, RequestCore)

export const lsAppGet = getMethod(LS_APP_DOMAIN)

class UnauthorizeHandler {
  static #isRefreshing = false
  static #waitingWueue = []

  static pushToWaitingQueue = (promiseObj) => {
    this.#waitingWueue.push(promiseObj)
  }

  static processWaitingQueue = ({ error, token }) => {
    this.#waitingWueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error)
      } else {
        resolve(token)
      }
    })
    this.#waitingWueue = []
  }

  static handleTokenRefresh = async (code, fetchArgs) => {
    if (typeof window === 'undefined') {
      // Only handled in client side
      throw new Error('尚未登入')
    }

    const [reqUrl, reqOptions] = fetchArgs

    // 檢查是否已登入且存有refresh token
    let refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken) {
      const originalAuth = reqOptions?.headers?.['authorization']
      if (!originalAuth) {
        throw new Error('尚未登入') // 從未登入
      } else {
        UnauthorizeHandler.handleRefreshFailed() // 有token卻沒有refresh token
      }
    }

    if (code === 401) {
      if (this.#isRefreshing) {
        // refresh中，將request放入queue等待完成
        return new Promise((resolve, reject) => {
          UnauthorizeHandler.pushToWaitingQueue({ resolve, reject })
        })
          .then((token) => {
            UnauthorizeHandler.setAuthToFetchArguments(fetchArgs, token)
            return lsRequest(...fetchArgs)
          })
          .catch((error) => Promise.reject(error))
      } else {
        // 開始refresh token
        this.#isRefreshing = true

        return new Promise((resolve, reject) => {
          fetch(`${LS_API_DOMAIN}/users/refreshToken`, {
            method: 'POST',
            headers: { 'Content-Type': 'Application/json' },
            body: JSON.stringify({
              refreshToken,
              origin: 'lsWeb',
              envInfo: getEnvInfo(),
            }),
          })
            .then(async (response) => {
              const data = await response.json()
              if (response.status === 200 && data.ok) {
                const newToken = data?.result?._id
                const newRefreshToken = data?.result?.refreshToken
                setToCookie('token', newToken)
                RequestCore.setToken(newToken)
                localStorage.setItem('refreshToken', newRefreshToken)
                // set token to native if it's lsApp
                if (/webview=lsApp/.test(location.href)) {
                  setTokenToNative(newToken)
                  setRefreshTokenToNative(newRefreshToken)
                }

                UnauthorizeHandler.setAuthToFetchArguments(fetchArgs, newToken)
                UnauthorizeHandler.processWaitingQueue({ token: newToken })
                resolve(lsRequest(...fetchArgs))
              } else {
                UnauthorizeHandler.handleRefreshFailed()
              }
            })
            .catch((error) => {
              UnauthorizeHandler.processWaitingQueue({ error, token: null })
              UnauthorizeHandler.handleRefreshFailed()
              reject(error)
            })
            .then(() => {
              this.#isRefreshing = false
            })
        })
      }
    }
  }

  static setAuthToFetchArguments = (fetchArgs = [], token) => {
    fetchArgs[1] = fetchArgs?.[1] || {}
    fetchArgs[1].headers = fetchArgs?.[1]?.headers || {}
    fetchArgs[1].headers['authorization'] = `Bearer ${token}`
  }

  static handleRefreshFailed = () => {
    // remove old tokens
    removeCookie('token')
    localStorage.removeItem('refreshToken')
    // route to login page
    const [currentPath, query] = location.href.split('?')
    let path = `/login?rt=${encodeURIComponent(currentPath)}`
    if (query) {
      path += `&${query}`
    }
    location.href = path
  }
}
